import { Link } from "wouter";
import PartnerLayout from "./PartnerLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Clock, TrendingUp, Repeat, Sparkles, Wallet, Star, Zap, Heart, Calendar, Target } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";

interface Campaign {
  id: string;
  templateType: string;
  name: string;
  status: string;
  budgetCents: number | null;
  spentCents: number;
  startDate: string | null;
  endDate: string | null;
  metrics: Record<string, number>;
}

interface KudozIncentive {
  id: string;
  templateType: string;
  name: string;
  status: string;
  kudozBudget: number | null;
  kudozSpent: number;
  startDate: string | null;
  endDate: string | null;
  metrics: Record<string, number>;
}

const GOAL_CARDS = [
  {
    id: 'new_customers',
    icon: Users,
    title: 'Bring in new customers',
    description: 'Welcome offer for first-time visitors',
    href: '/partner/campaigns',
    color: '#6366f1',
  },
  {
    id: 'slow_periods',
    icon: Clock,
    title: 'Fill slow periods',
    description: 'Boost visits during quiet times',
    href: '/partner/campaigns',
    color: '#f59e0b',
  },
  {
    id: 'loyal_customers',
    icon: Repeat,
    title: 'Reward loyal customers',
    description: 'Kudoz program for repeat visitors',
    href: '/partner/kudoz-incentives',
    color: '#10b981',
  },
  {
    id: 'kudoz_promo',
    icon: Zap,
    title: 'Run a Kudoz promo',
    description: 'Multiply Kudoz earned on visits',
    href: '/partner/kudoz-incentives',
    color: '#f97316',
  },
];

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
    active: 'default',
    paused: 'outline',
    completed: 'secondary',
    expired: 'destructive',
    draft: 'secondary',
  };
  return <Badge variant={variants[status] || 'secondary'}>{status.charAt(0).toUpperCase() + status.slice(1)}</Badge>;
}

export default function PartnerLoyalty() {
  const restaurantId = localStorage.getItem('partnerRestaurantId') || '';
  const eatzyToken = sessionStorage.getItem('partnerEatzyToken');

  const { data: campaigns, isLoading: campaignsLoading } = useQuery<Campaign[]>({
    queryKey: ['/api/partner/campaigns', restaurantId],
    queryFn: async () => {
      const res = await fetch(`/api/partner/campaigns/${restaurantId}`, {
        headers: { 'X-Eatzy-Token': eatzyToken || '' },
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!restaurantId && !!eatzyToken,
  });

  const { data: incentives, isLoading: incentivesLoading } = useQuery<KudozIncentive[]>({
    queryKey: ['/api/partner/kudoz-incentives', restaurantId],
    queryFn: async () => {
      const res = await fetch(`/api/partner/kudoz-incentives/${restaurantId}`, {
        headers: { 'X-Eatzy-Token': eatzyToken || '' },
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!restaurantId && !!eatzyToken,
  });

  const activeCampaigns = campaigns?.filter(c => c.status === 'active') || [];
  const activeIncentives = incentives?.filter(i => i.status === 'active') || [];
  const hasActive = activeCampaigns.length > 0 || activeIncentives.length > 0;
  const isLoading = campaignsLoading || incentivesLoading;

  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-loyalty-title">Loyalty</h1>
          <p className="text-muted-foreground">Manage your campaigns and Kudoz programs</p>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : !hasActive ? (
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Pick your goal</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {GOAL_CARDS.map((goal) => {
                const Icon = goal.icon;
                return (
                  <Link key={goal.id} href={goal.href}>
                    <div
                      className="flex items-center gap-4 p-4 rounded-lg border hover-elevate cursor-pointer"
                      data-testid={`goal-card-${goal.id}`}
                    >
                      <div className="p-2 rounded-md shrink-0" style={{ backgroundColor: `${goal.color}18` }}>
                        <Icon className="h-5 w-5" style={{ color: goal.color }} />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{goal.title}</p>
                        <p className="text-xs text-muted-foreground">{goal.description}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Active</p>
              <div className="space-y-3">
                {activeCampaigns.map(c => (
                  <div key={c.id} className="flex items-center gap-3 p-4 rounded-lg border bg-muted/30">
                    <Target className="h-5 w-5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground">Campaign</p>
                    </div>
                    <StatusBadge status={c.status} />
                  </div>
                ))}
                {activeIncentives.map(i => (
                  <div key={i.id} className="flex items-center gap-3 p-4 rounded-lg border bg-muted/30">
                    <Zap className="h-5 w-5 text-yellow-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{i.name}</p>
                      <p className="text-xs text-muted-foreground">Kudoz Program</p>
                    </div>
                    <StatusBadge status={i.status} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <Tabs defaultValue="campaigns">
          <TabsList className="w-full">
            <TabsTrigger value="campaigns" className="flex-1">Campaigns</TabsTrigger>
            <TabsTrigger value="kudoz" className="flex-1">Kudoz Programs</TabsTrigger>
          </TabsList>

          <TabsContent value="campaigns" className="mt-4 space-y-3">
            <div className="flex justify-end">
              <Link href="/partner/campaigns">
                <Button size="sm" data-testid="button-manage-campaigns">
                  <Zap className="h-4 w-4 mr-1" />
                  Manage Campaigns
                </Button>
              </Link>
            </div>
            {campaignsLoading ? (
              [1, 2].map(i => <Skeleton key={i} className="h-20 w-full" />)
            ) : campaigns && campaigns.length > 0 ? (
              campaigns.map(c => (
                <Card key={c.id} data-testid={`campaign-row-${c.id}`}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <Target className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div>
                          <p className="font-semibold text-sm">{c.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {c.metrics?.conversions ?? 0} conversions
                            {c.budgetCents ? ` · $${(c.spentCents / 100).toFixed(0)} / $${(c.budgetCents / 100).toFixed(0)}` : ''}
                          </p>
                        </div>
                      </div>
                      <StatusBadge status={c.status} />
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-10">
                  <Target className="h-10 w-10 text-muted-foreground mb-3" />
                  <p className="font-medium">No campaigns yet</p>
                  <p className="text-sm text-muted-foreground text-center mt-1">Launch a campaign to attract and retain customers</p>
                  <Link href="/partner/campaigns" className="mt-4">
                    <Button size="sm">Create Campaign</Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="kudoz" className="mt-4 space-y-3">
            <div className="flex justify-end">
              <Link href="/partner/kudoz-incentives">
                <Button size="sm" data-testid="button-manage-incentives">
                  <Zap className="h-4 w-4 mr-1" />
                  Manage Programs
                </Button>
              </Link>
            </div>
            {incentivesLoading ? (
              [1, 2].map(i => <Skeleton key={i} className="h-20 w-full" />)
            ) : incentives && incentives.length > 0 ? (
              incentives.map(inc => (
                <Card key={inc.id} data-testid={`incentive-row-${inc.id}`}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <Zap className="h-4 w-4 text-yellow-500 shrink-0" />
                        <div>
                          <p className="font-semibold text-sm">{inc.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {inc.kudozBudget
                              ? `${inc.kudozSpent.toLocaleString()} / ${inc.kudozBudget.toLocaleString()} Kudoz`
                              : 'No budget limit'}
                          </p>
                        </div>
                      </div>
                      <StatusBadge status={inc.status} />
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-10">
                  <Zap className="h-10 w-10 text-muted-foreground mb-3" />
                  <p className="font-medium">No Kudoz programs yet</p>
                  <p className="text-sm text-muted-foreground text-center mt-1">Create a Kudoz incentive to reward loyal customers</p>
                  <Link href="/partner/kudoz-incentives" className="mt-4">
                    <Button size="sm">Create Program</Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </PartnerLayout>
  );
}
